import { useCallback, useState } from 'react';
import type { WhatsAppHandoff } from '../types/events';

const STORAGE_KEY_PREFIX = 'cp-widget-handoff-dismissed-';

function storageKey(widgetId: string | undefined): string | null {
  return widgetId ? `${STORAGE_KEY_PREFIX}${widgetId}` : null;
}

function readDismissed(widgetId: string | undefined): boolean {
  const key = storageKey(widgetId);
  if (!key) return false;
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(widgetId: string | undefined, dismissed: boolean): void {
  const key = storageKey(widgetId);
  if (!key) return;
  try {
    if (dismissed) {
      sessionStorage.setItem(key, '1');
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Storage blocked: the dismissal then lasts only as long as this page.
  }
}

export interface UseWhatsAppHandoffBannerReturn {
  /** The banner to render, or null when there is none or the visitor dismissed it. */
  banner: WhatsAppHandoff | null;
  /** Whatever chat_settings says is available right now (null takes it away). */
  setAvailable: (handoff: WhatsAppHandoff | null) => void;
  /** A `whatsapp_handoff` push; `raise` re-opens a dismissed banner. */
  offer: (handoff: WhatsAppHandoff, raise: boolean) => void;
  dismiss: () => void;
}

/**
 * The "continue on WhatsApp" banner's state (CAIV-1063).
 *
 * The server decides whether there is a banner; this only remembers whether the visitor
 * closed it. Dismissal holds for the browser session and is keyed by the widget id, not
 * the session id: the backend rotates the session id when it cannot restore one, which
 * would silently reset the dismissal and pop the banner back. A new attention episode
 * (the assistant failed them) re-opens it once, which the server signals with `raise`.
 */
export function useWhatsAppHandoffBanner(
  widgetId: string | undefined
): UseWhatsAppHandoffBannerReturn {
  const [handoff, setHandoff] = useState<WhatsAppHandoff | null>(null);
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed(widgetId));

  const setAvailable = useCallback((next: WhatsAppHandoff | null) => {
    setHandoff(next);
  }, []);

  const offer = useCallback(
    (next: WhatsAppHandoff, raise: boolean) => {
      setHandoff(next);
      if (raise) {
        setDismissed(false);
        writeDismissed(widgetId, false);
      }
    },
    [widgetId]
  );

  const dismiss = useCallback(() => {
    setDismissed(true);
    writeDismissed(widgetId, true);
  }, [widgetId]);

  return {
    banner: dismissed ? null : handoff,
    setAvailable,
    offer,
    dismiss,
  };
}
